import { OTHER_PAYMENT_OPTION_ID } from '@webapp/components/RequestableCreditCardSelect'
import {
  computeExpenseLineItemTotalsUseCase,
  round2,
} from '@webapp/modules/expense/business-logic/use-cases/ComputeExpenseLineItemTotals'
import {
  type ExpenseTaxSelectValue,
  isPresetTax,
  resolveTaxMode,
} from '@webapp/modules/expense/components/ExpenseTaxSelect'
import {
  ExpensePaymentTypes,
  type ExpensePaymentTypeEnum,
} from '@webapp/modules/expense/entities/enums/ExpensePaymentTypeEnum'
import { ExpenseTypes } from '@webapp/modules/expense/entities/enums/ExpenseTypeEnum'
import { type ExpenseItem } from '@webapp/modules/expense/entities/ExpenseItem'
import { type ExpenseLineItemFormSchema } from './ExpenseLineItemForm'
import { type ExpenseItemFormSchema } from '../ExpenseLineItemDrawer/types'

const buildExpenseTypeData = (
  values: ExpenseLineItemFormSchema
): ExpenseItemFormSchema['expenseTypeData'] => {
  switch (values.expenseType) {
    case ExpenseTypes.MEALS:
      return { type: 'MEALS', data: { tips: values.tips ?? 0 } }
    case ExpenseTypes.MILEAGE:
      return {
        type: 'MILEAGE',
        data: {
          mileageRate: values.mileageRate ?? 0,
          distance: values.distance ?? 0,
          unitOfDistance: values.unitOfDistanceStr.value as 'km' | 'mi',
        },
      }
    case ExpenseTypes.LABOUR:
      return {
        type: 'LABOUR',
        data: {
          hourlyRate: values.hourlyRate ?? 0,
          hoursOfWork: values.hoursOfWork ?? 0,
        },
      }
    case ExpenseTypes.AIRFARE:
      return {
        type: 'AIRFARE',
        data: {
          airline: values.airline,
          ticketNumber: values.ticketNumber,
          departureDate: values.departureDate
            ? values.departureDate.format('YYYY-MM-DD')
            : null,
          arrivalDate: values.arrivalDate
            ? values.arrivalDate.format('YYYY-MM-DD')
            : null,
        },
      }
    case ExpenseTypes.LODGING:
      return { type: 'LODGING', data: null }
    default:
      return { type: 'OTHER', data: null }
  }
}

const buildExpenseItemTaxMode = (
  taxTypeValue: ExpenseTaxSelectValue
): ExpenseItemFormSchema['taxMode'] => {
  if (isPresetTax(taxTypeValue)) {
    return 'saved'
  }
  if (taxTypeValue.id === '$') {
    return 'dollar'
  }
  return 'percent'
}

const buildExpenseItemTaxPercentage = (
  taxTypeValue: ExpenseTaxSelectValue,
  isManualPercent: boolean,
  values: ExpenseLineItemFormSchema
): string => {
  if (isPresetTax(taxTypeValue)) {
    return taxTypeValue.percentage
  }
  if (isManualPercent) {
    return String((values.customTaxPercentage ?? 0) / 100)
  }
  if (taxTypeValue.id === '%') {
    return String((values.taxAmount ?? 0) / 100)
  }
  return '0'
}

export const mapFormSchemaToExpenseItemFormSchema = (
  values: ExpenseLineItemFormSchema,
  item: ExpenseItem | undefined,
  isReclaimableMileageTaxEnabled = false
): ExpenseItemFormSchema => {
  if (values.currency == null) {
    // Yup validation requires currency before submit, so this path only
    // fires when validation has been bypassed.
    throw new Error('ExpenseLineItemForm submitted without a currency')
  }

  const taxTypeValue = values.taxType
  const isTaxProfile = isPresetTax(taxTypeValue)
  const isTaxCustomPercent =
    !isTaxProfile &&
    taxTypeValue.id === '%' &&
    values.customTaxPercentage != null
  const isOverridableDollar = isTaxProfile || isTaxCustomPercent
  const isReclaimableMileageTax =
    isReclaimableMileageTaxEnabled &&
    values.expenseType === ExpenseTypes.MILEAGE

  const { computedSubtotal, computedTaxAmount, computedTotal } =
    computeExpenseLineItemTotalsUseCase.execute({
      expenseType: values.expenseType,
      subtotal: values.subtotal,
      tax: isReclaimableMileageTax
        ? { mode: 'dollar', amount: values.taxAmount ?? 0 }
        : resolveTaxMode({
            taxType: taxTypeValue,
            taxAmount: values.taxAmount,
            customTaxPercentage: values.customTaxPercentage,
            isOverridden: false,
          }),
      tips: values.tips ?? 0,
      mileageRate: values.mileageRate ?? 0,
      distance: values.distance ?? 0,
      hourlyRate: values.hourlyRate ?? 0,
      hoursOfWork: values.hoursOfWork ?? 0,
      isReclaimableMileageTaxEnabled,
    })

  const overridableTaxAmount = values.isTaxAmountOverridden
    ? (values.taxAmount ?? 0)
    : computedTaxAmount
  const overridableTotal = round2(
    computedSubtotal +
      overridableTaxAmount +
      (values.expenseType === ExpenseTypes.MEALS ? (values.tips ?? 0) : 0)
  )

  // Use overridden tax amount if it exists and the expense type is not reclaimable mileage
  let finalTaxAmount = computedTaxAmount
  if (!isReclaimableMileageTax && isOverridableDollar) {
    finalTaxAmount = overridableTaxAmount
  }
  let finalTotal = computedTotal
  if (!isReclaimableMileageTax && isOverridableDollar) {
    finalTotal = overridableTotal
  }

  // When reclaimable_expense_mileage_tax is enabled, mileage tax is included in the subtotal
  const reclaimableTax = isReclaimableMileageTax
    ? values.mileageRatePreset?.tax
    : null

  const taxMode: ExpenseItemFormSchema['taxMode'] = isReclaimableMileageTax
    ? 'saved'
    : buildExpenseItemTaxMode(taxTypeValue)

  let taxName: string
  if (isReclaimableMileageTax) {
    taxName = reclaimableTax?.name ?? ''
  } else if (isTaxProfile) {
    taxName = taxTypeValue.name
  } else {
    taxName = taxTypeValue.id
  }

  const taxPercentage = isReclaimableMileageTax
    ? (reclaimableTax?.percentage ?? '0')
    : buildExpenseItemTaxPercentage(taxTypeValue, isTaxCustomPercent, values)
  let taxId: ExpenseItemFormSchema['taxId']
  if (isReclaimableMileageTax) {
    taxId = reclaimableTax?.id ?? null
  } else if (isTaxProfile) {
    taxId = taxTypeValue.id
  } else {
    taxId = null
  }

  let taxValue: number
  if (isReclaimableMileageTax) {
    taxValue = finalTaxAmount
  } else if (isTaxCustomPercent) {
    taxValue = values.customTaxPercentage ?? 0
  } else {
    taxValue = values.taxAmount ?? 0
  }

  let taxIsInclusive: boolean
  if (isReclaimableMileageTax) {
    taxIsInclusive = reclaimableTax?.isInclusive ?? true
  } else if (isTaxProfile) {
    taxIsInclusive = taxTypeValue.isInclusive
  } else {
    taxIsInclusive = false
  }

  // Reimbursable items do not collect a payment method from the UI, so any
  // stale selection is cleared and the original paymentType is preserved on
  // edit (OTHER for new items). For non-reimbursable items with no selection
  // (e.g. CASH/CHEQUE on edit, which have no matching Select option), preserve
  // the item's original paymentType rather than silently coercing to OTHER.
  const resolvedPaymentMethod = values.reimbursable
    ? null
    : (values.paymentMethod ?? null)

  const resolvedPaymentType: ExpensePaymentTypeEnum = (() => {
    if (resolvedPaymentMethod == null) {
      return item?.paymentType ?? ExpensePaymentTypes.OTHER
    }
    if (resolvedPaymentMethod.id === OTHER_PAYMENT_OPTION_ID) {
      return ExpensePaymentTypes.OTHER
    }
    return ExpensePaymentTypes.CREDIT_CARD_ASSIGNED
  })()

  return {
    id: item?.id ?? null,
    merchant: values.merchant,
    account: values.account ?? null,
    currency: values.currency,
    reimbursable: values.reimbursable,
    expenseType: values.expenseType,
    paymentType: resolvedPaymentType,
    paymentMethod: resolvedPaymentMethod,
    amount: String(finalTotal),
    subtotal: String(computedSubtotal),
    taxMode,
    taxValue: String(taxValue),
    taxAmount: String(finalTaxAmount),
    isTaxAmountOverridden: values.isTaxAmountOverridden,
    isReclaimableMileageTax,
    taxId,
    taxName,
    taxPercentage,
    taxIsInclusive,
    transactionDate: values.transDate
      ? values.transDate.format('YYYY-MM-DD')
      : '',
    attachment: values.attachment,
    notes: values.notes,
    expenseTypeData: buildExpenseTypeData(values),
    mileageRatePreset:
      values.expenseType === ExpenseTypes.MILEAGE
        ? values.mileageRatePreset
        : null,
  }
}
